import React from "react";
import { formattedDateTime } from "../../util/date";
import useTranslation from "../../i18n/useTranslation";

export default function GameSheet({ event, division, activeSeasonData }) {
    const { t, language } = useTranslation();

    if (!event || !event.game) {
        return null;
    }

    const game = event.game;

    const homeName =
        game.homeTeam?.name ||
        game.homeTeamAlias ||
        t("gameSheet.fallbacks.homeTeam");

    const visitorName =
        game.visitorTeam?.name ||
        game.visitorTeamAlias ||
        t("gameSheet.fallbacks.awayTeam");

    const matchDayLabel =
        game.matchDay != null
            ? t("gameSheet.matchweekLabel", { matchDay: game.matchDay })
            : t("gameSheet.fallbacks.match");

    const stageName = event.stage?.name || "";
    const groupName = game.group?.name || event.stage?.groups?.[0]?.name || "";
    const stageGroupLabel = [stageName, groupName].filter(Boolean).join(" • ");

    const dateLabel = formattedDateTime(
        event.date,
        event.timezone,
        language || "en",
        "EEEE, LLL d, yyyy"
    );

    const timeLabel = formattedDateTime(
        event.date,
        event.timezone,
        language || "en",
        "h:mm a"
    );

    const fieldName = event.field?.name || t("gameSheet.fallbacks.emptyValue");

    const locationParts = [
        event.address,
        event.city,
        event.state,
        event.country,
    ].filter(Boolean);

    const locationLabel =
        locationParts.join(", ") || t("gameSheet.fallbacks.emptyValue");

    const homePlayers = game.homeTeam?.players || [];
    const visitorPlayers = game.visitorTeam?.players || [];

    const responsibles = event?.responsibles || [];
    const getOfficialName = (idx) => responsibles[idx]?.fullName || "";
    const extraOfficials = responsibles.slice(6);

    const pageStyle = {
        fontFamily: "Poppins, sans-serif",
        padding: "10mm 8mm",
        maxWidth: "100%",
        margin: "0 auto",
        fontSize: "12px",
        color: "#000",
        boxSizing: "border-box",
    };

    return (
        <>
            <div className="kbola-gamesheet-page" style={pageStyle}>
                <header
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                        borderBottom: "2px solid #000",
                        paddingBottom: 8,
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 900,
                                textTransform: "uppercase",
                            }}
                        >
                            {division?.organization?.name ||
                                t("gameSheet.fallbacks.organization")}
                        </div>

                        <div style={{ fontSize: 12 }}>
                            <strong>{t("gameSheet.header.division")}: </strong>
                            {division?.name || t("gameSheet.fallbacks.emptyValue")}
                        </div>

                        <div style={{ fontSize: 12 }}>
                            <strong>{t("gameSheet.header.season")}: </strong>
                            {activeSeasonData?.name || t("gameSheet.fallbacks.emptyValue")}
                        </div>

                        {stageGroupLabel && (
                            <div style={{ fontSize: 11 }}>
                                <strong>{t("gameSheet.header.stageGroup")}: </strong>
                                {stageGroupLabel}
                            </div>
                        )}

                        <div
                            style={{
                                fontSize: 11,
                                marginTop: 2,
                                fontWeight: 600,
                            }}
                        >
                            {matchDayLabel}
                        </div>
                    </div>

                    <div
                        style={{
                            textAlign: "right",
                            fontSize: 11,
                        }}
                    >
                        <div>
                            <strong>{t("gameSheet.header.date")}:</strong> {dateLabel}
                        </div>
                        <div>
                            <strong>{t("gameSheet.header.time")}:</strong> {timeLabel}
                        </div>
                        <div>
                            <strong>{t("gameSheet.header.field")}:</strong> {fieldName}
                        </div>
                        <div>
                            <strong>{t("gameSheet.header.location")}:</strong>{" "}
                            {locationLabel}
                        </div>
                    </div>
                </header>

                <section
                    style={{
                        border: "1px solid #000",
                        borderRadius: 6,
                        padding: 10,
                        marginBottom: 12,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                textAlign: "left",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                }}
                            >
                                {t("gameSheet.teams.away")}
                            </div>
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 900,
                                    borderBottom: "1px solid #000",
                                    paddingBottom: 2,
                                }}
                            >
                                {visitorName}
                            </div>
                        </div>

                        <div
                            style={{
                                width: 140,
                                textAlign: "center",
                                fontWeight: 900,
                                fontSize: 14,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    textTransform: "uppercase",
                                    marginBottom: 2,
                                }}
                            >
                                {t("gameSheet.score.finalScore")}
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    border: "1px solid #000",
                                    borderRadius: 4,
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        flex: 1,
                                        padding: "6px 0",
                                        borderRight: "1px solid #000",
                                    }}
                                >
                                    <span style={{ padding: "0 8px" }}>__</span>
                                </div>

                                <div style={{ flex: 1, padding: "6px 0" }}>
                                    <span style={{ padding: "0 8px" }}>__</span>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                flex: 1,
                                textAlign: "right",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10,
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                }}
                            >
                                {t("gameSheet.teams.home")}
                            </div>
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 900,
                                    borderBottom: "1px solid #000",
                                    paddingBottom: 2,
                                }}
                            >
                                {homeName}
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 10,
                            marginTop: 4,
                        }}
                    >
                        <div>
                            {t("gameSheet.notes.weather")}:{" "}
                            <span
                                style={{
                                    display: "inline-block",
                                    minWidth: 120,
                                    borderBottom: "1px solid #000",
                                }}
                            >
                                &nbsp;
                            </span>
                        </div>
                        <div>
                            {t("gameSheet.notes.category")}:{" "}
                            <span
                                style={{
                                    display: "inline-block",
                                    minWidth: 120,
                                    borderBottom: "1px solid #000",
                                }}
                            >
                                &nbsp;
                            </span>
                        </div>
                    </div>

                    <div
                        style={{
                            fontSize: 10,
                            marginTop: 4,
                            fontStyle: "italic",
                            color: "#333",
                        }}
                    >
                        {t("gameSheet.notes.pageHint")}
                    </div>
                </section>

                <section
                    style={{
                        display: "flex",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontWeight: 800,
                                fontSize: 11,
                                marginBottom: 4,
                                textTransform: "uppercase",
                            }}
                        >
                            {t("gameSheet.rosters.awayRoster")}
                        </div>
                        <RosterTable t={t} players={visitorPlayers} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontWeight: 800,
                                fontSize: 11,
                                marginBottom: 4,
                                textTransform: "uppercase",
                            }}
                        >
                            {t("gameSheet.rosters.homeRoster")}
                        </div>
                        <RosterTable t={t} players={homePlayers} />
                    </div>
                </section>

                <section
                    style={{
                        border: "1px solid #000",
                        borderRadius: 6,
                        padding: 8,
                        marginBottom: 4,
                        fontSize: 11,
                    }}
                >
                    <div
                        style={{
                            fontWeight: 800,
                            fontSize: 11,
                            marginBottom: 4,
                            textTransform: "uppercase",
                        }}
                    >
                        {t("gameSheet.officials.title")}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        <OfficialLine
                            label={t("gameSheet.officials.refereeCrewChief")}
                            name={getOfficialName(0)}
                        />
                        <OfficialLine
                            label={t("gameSheet.officials.assistantUmpire1")}
                            name={getOfficialName(1)}
                        />
                        <OfficialLine
                            label={t("gameSheet.officials.assistantUmpire2")}
                            name={getOfficialName(2)}
                        />
                        <OfficialLine
                            label={t("gameSheet.officials.fourthOfficialUmpire3")}
                            name={getOfficialName(3)}
                        />
                        <OfficialLine
                            label={t("gameSheet.officials.scorerTableOfficial")}
                            name={getOfficialName(4)}
                        />
                        <OfficialLine
                            label={t("gameSheet.officials.timekeeper")}
                            name={getOfficialName(5)}
                        />
                    </div>

                    {extraOfficials.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                            <span style={{ fontWeight: 600 }}>
                                {t("gameSheet.officials.otherOfficials")}:{" "}
                            </span>
                            <span
                                style={{
                                    display: "inline-block",
                                    borderBottom: "1px solid #000",
                                    minWidth: 200,
                                    padding: "0 2px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {extraOfficials.map((o) => o.fullName).join(", ")}
                            </span>
                        </div>
                    )}
                </section>
            </div>

            <div className="kbola-gamesheet-page" style={pageStyle}>
                <section
                    style={{
                        marginBottom: 12,
                        fontSize: 11,
                    }}
                >
                    <div
                        style={{
                            fontWeight: 800,
                            fontSize: 11,
                            marginBottom: 6,
                            textTransform: "uppercase",
                        }}
                    >
                        {t("gameSheet.incidents.title")}
                    </div>

                    <table
                        style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "1px solid #000",
                        }}
                    >
                        <thead>
                        <tr>
                            <th style={cellHeadStyle}>
                                {t("gameSheet.incidents.timeSegment")}
                            </th>
                            <th style={cellHeadStyle}>
                                {t("gameSheet.incidents.team")}
                            </th>
                            <th style={cellHeadStyle}>
                                {t("gameSheet.incidents.player")}
                            </th>
                            <th style={cellHeadStyle}>
                                {t("gameSheet.incidents.eventDescription")}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {Array.from({ length: 40 }).map((_, idx) => (
                            <tr key={idx}>
                                <td style={cellBodyStyle}>&nbsp;</td>
                                <td style={cellBodyStyle}>&nbsp;</td>
                                <td style={cellBodyStyle}>&nbsp;</td>
                                <td style={cellBodyStyle}>&nbsp;</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </section>

                <section style={{ fontSize: 11 }}>
                    <div
                        style={{
                            fontWeight: 800,
                            fontSize: 11,
                            marginBottom: 4,
                            textTransform: "uppercase",
                        }}
                    >
                        {t("gameSheet.signatures.title")}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                            gap: 24,
                        }}
                    >
                        <SignatureLine
                            label={t("gameSheet.signatures.awayRepresentative")}
                        />
                        <SignatureLine
                            label={t("gameSheet.signatures.homeRepresentative")}
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                            gap: 24,
                        }}
                    >
                        <SignatureLine
                            label={t("gameSheet.signatures.refereeCrewChief")}
                        />
                        <SignatureLine
                            label={t(
                                "gameSheet.signatures.leagueRepresentativeOptional"
                            )}
                        />
                    </div>

                    <div>
                        {t("gameSheet.signatures.commentsProtests")}:
                        <div
                            style={{
                                border: "1px solid #000",
                                borderRadius: 4,
                                minHeight: 200,
                                marginTop: 2,
                            }}
                        />
                    </div>
                </section>
            </div>
        </>
    );
}

const cellHeadStyle = {
    border: "1px solid #000",
    padding: "3px 4px",
    fontSize: "10px",
    fontWeight: 700,
    textAlign: "left",
};

const cellBodyStyle = {
    border: "1px solid #000",
    padding: "3px 4px",
    fontSize: "10px",
    height: 18,
};

function RosterTable({ players, t }) {
    const TOTAL_ROWS = 22;
    const safePlayers = Array.isArray(players) ? players : [];

    const rows = Array.from({ length: TOTAL_ROWS }, (_, idx) => {
        return safePlayers[idx] || null;
    });

    return (
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #000",
            }}
        >
            <thead>
            <tr>
                <th style={cellHeadStyle}>{t("gameSheet.rosters.number")}</th>
                <th style={cellHeadStyle}>{t("gameSheet.rosters.player")}</th>
                <th style={cellHeadStyle}>{t("gameSheet.rosters.position")}</th>
                <th style={cellHeadStyle}>{t("gameSheet.rosters.starter")}</th>
                <th style={cellHeadStyle}>{t("gameSheet.rosters.notes")}</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((player, idx) => (
                <tr key={player?.playerId || `empty-${idx}`}>
                    <td style={cellBodyStyle}>{player?.shirtNumber || ""}</td>
                    <td style={cellBodyStyle}>{player?.fullName || ""}</td>
                    <td style={cellBodyStyle}>&nbsp;</td>
                    <td style={cellBodyStyle}>&nbsp;</td>
                    <td style={cellBodyStyle}>&nbsp;</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

function OfficialLine({ label, name }) {
    return (
        <div style={{ minWidth: 230, marginBottom: 2 }}>
            <span style={{ fontWeight: 600 }}>{label}: </span>
            <span
                style={{
                    display: "inline-block",
                    borderBottom: "1px solid #000",
                    minWidth: 140,
                    padding: "0 2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {name || "\u00A0"}
            </span>
        </div>
    );
}

function SignatureLine({ label }) {
    return (
        <div style={{ flex: 1 }}>
            <div
                style={{
                    borderBottom: "1px solid #000",
                    marginBottom: 2,
                    height: 18,
                }}
            />
            <div style={{ fontSize: 10, textAlign: "center" }}>{label}</div>
        </div>
    );
}